/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IDetailComponentProps,
  IUtilityBillBreakdownAPIResponse
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  BreakdownChartsComponent,
  IBreakdownChartsComponentProps
} from './BreakdownCharts/BreakdownChartsComponent';

interface IUtilityBillBreakdown extends IBreakdownChartsComponentProps {
  onRefresh: () => Promise<void>;
  isMonthly?: boolean;
}

const UtilityBreakdownChartContent = (props: IUtilityBillBreakdown) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { chartData, currencyCode, utilityType, onRefresh, isMonthly } = props;
  if (isObjectOrArrayAndEmpty(chartData)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <BreakdownChartsComponent
        utilityType={utilityType}
        currencyCode={currencyCode}
        chartData={chartData}
        isMonthly={isMonthly ? isMonthly : false}
      />
    </div>
  );
};

const UtilityBreakdownChartWithLoader = withLoadingStatus(
  withErrorStatus(UtilityBreakdownChartContent)
);

interface IUtilityBillBreakdownChartWidgetProps extends IDetailComponentProps {
  utilityType: string;
  isMonthly?: boolean;
}
const UtilityBillBreakdownChartWidget = (
  props: IUtilityBillBreakdownChartWidgetProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, utilityType, isMonthly } = props;

  interface IUtilityBillBreakdownExtended {
    data: IUtilityBillBreakdownAPIResponse;
  }

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const utilityBillBreakdownData = (all as unknown) as IUtilityBillBreakdownExtended;
  const isDataAvailable = utilityBillBreakdownData ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityBillBreakdownExtended);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(utilityBillBreakdownData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <UtilityBreakdownChartWithLoader
        loading={isLoading}
        error={errorInfo}
        statusCode={statusCode}
        onRefresh={onRefresh}
        loadingText={t(d.LOADING)}
        title={t(d.CONNECTIONERRORTITLE)}
        noDataMessage={t(d.NODATAAVAILABLE)}
        connectionErrorMessage={t(d.CONNECTIONERROR)}
        chartData={data?.data?.utilityAggregations}
        utilityType={utilityType}
        currencyCode={data?.data?.utilityCostCurrency}
        isMonthly={isMonthly ? isMonthly : false}
      />
    </>
  );
};
export default UtilityBillBreakdownChartWidget;
